import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Mail } from 'lucide-react';
import { services, serviceCategories } from '../data/services';

export const Header = ({ onOpenLeadPopup }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Our Attorneys', path: '/attorneys' },
    { name: 'Services', path: '#', hasDropdown: true },
    { name: 'Contact Us', path: '/contact' }
  ];

  return (
    <>
      {/* Main Header */}
      <header 
        className={`sticky top-0 z-50 bg-white transition-all duration-300 ${
          isScrolled ? 'shadow-md' : 'border-b border-gray-100'
        }`}
        data-testid="main-header"
      >
        <div className="le-container">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img src="/logo-nav.png" alt="Legal Emphasis" className="h-12" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <div key={link.name} className="relative nav-item group">
                  {link.hasDropdown ? (
                    <button 
                      className="flex items-center gap-1 text-sm font-medium text-[#333333] hover:text-[#0A2A5E] transition-colors uppercase tracking-wider"
                      data-testid="services-dropdown-trigger"
                    >
                      {link.name}
                      <ChevronDown size={16} className="transition-transform group-hover:rotate-180" />
                    </button>
                  ) : (
                    <Link 
                      to={link.path}
                      className={`text-sm font-medium uppercase tracking-wider transition-colors ${
                        location.pathname === link.path 
                          ? 'text-[#0A2A5E]' 
                          : 'text-[#333333] hover:text-[#0A2A5E]'
                      }`}
                      data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  )}

                  {/* Services Dropdown */}
                  {link.hasDropdown && (
                    <div className="nav-dropdown p-6" data-testid="services-dropdown">
                      <div className="grid grid-cols-2 gap-6 min-w-[500px]">
                        {serviceCategories.map((category) => (
                          <div key={category.name}>
                            <h4 className="font-semibold text-[#0A2A5E] mb-3 text-sm uppercase tracking-wider">
                              {category.name}
                            </h4>
                            <ul className="space-y-2">
                              {category.services.map((service) => (
                                <li key={service.id}>
                                  <Link
                                    to={`/services/${service.slug}`}
                                    className="text-sm text-gray-600 hover:text-[#0A2A5E] transition-colors block py-1"
                                    data-testid={`dropdown-${service.slug}`}
                                  >
                                    {service.title}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <button 
                onClick={onOpenLeadPopup}
                className="le-btn-primary btn-lift"
                data-testid="header-cta-btn"
              >
                Get Legal Help
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div 
          className={`lg:hidden fixed inset-0 top-20 bg-white z-40 transform transition-transform duration-300 overflow-y-auto ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          data-testid="mobile-menu"
        >
          <div className="p-6">
            <nav className="space-y-4">
              {navLinks.map((link) => (
                <div key={link.name}>
                  {link.hasDropdown ? (
                    <div>
                      <button 
                        onClick={() => setIsServicesOpen(!isServicesOpen)}
                        className="flex items-center justify-between w-full py-3 text-lg font-medium text-[#333333] border-b border-gray-100"
                        data-testid="mobile-services-toggle"
                      >
                        {link.name}
                        <ChevronDown 
                          size={20} 
                          className={`transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} 
                        />
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${isServicesOpen ? 'max-h-[800px]' : 'max-h-0'}`}>
                        <div className="py-4 pl-4 space-y-4">
                          {serviceCategories.map((category) => (
                            <div key={category.name}>
                              <h4 className="font-semibold text-[#0A2A5E] mb-2 text-sm uppercase">
                                {category.name}
                              </h4>
                              <ul className="space-y-2">
                                {category.services.map((service) => (
                                  <li key={service.id}>
                                    <Link
                                      to={`/services/${service.slug}`}
                                      className="text-gray-600 hover:text-[#0A2A5E] block py-1"
                                      data-testid={`mobile-${service.slug}`}
                                    >
                                      {service.title}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Link 
                      to={link.path}
                      className="block py-3 text-lg font-medium text-[#333333] border-b border-gray-100"
                      data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            <div className="mt-8">
              <button 
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onOpenLeadPopup();
                }}
                className="le-btn-primary w-full justify-center"
                data-testid="mobile-cta-btn"
              >
                Get Legal Help
              </button>
            </div>

            <div className="mt-8 pt-8 border-t border-gray-100 space-y-4">
              <a href="tel:+18887505204" className="flex items-center gap-3 text-gray-600">
                <Phone size={18} />
                <span>+1 (888) 750-5204</span>
              </a>
              <a href="mailto:info@legalemphasis.com" className="flex items-center gap-3 text-gray-600">
                <Mail size={18} />
                <span>info@legalemphasis.com</span>
              </a>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};
